import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function Philosophy() {
    const containerRef = useRef(null);
    const textRef = useRef(null);

    useEffect(() => {
        let ctx = gsap.context(() => {
            // Main statement reveal (Word by Word dramatic fade up)
            const words = gsap.utils.toArray('.word-reveal');
            gsap.set(words, { y: 60, opacity: 0, scale: 0.95 });

            ScrollTrigger.batch(words, {
                start: "top 85%",
                onEnter: (elements) => {
                    gsap.to(elements, {
                        y: 0,
                        opacity: 1,
                        scale: 1,
                        duration: 1.2,
                        stagger: 0.08,
                        ease: "power3.out",
                        overwrite: true
                    });
                }
            });

            // Dramatic slow Parallax for the abstract background
            gsap.to(".parallax-bg", {
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: 1.5 // Added scrub smoothing for "heavy" feel
                },
                y: "30%",
                scale: 1.1, // Slight zoom as you scroll
                ease: "none"
            });

            // Dynamic Text Highlighter
            const highlighters = gsap.utils.toArray('.highlight-text');
            highlighters.forEach(mark => {
                gsap.to(mark, {
                    scrollTrigger: {
                        trigger: mark,
                        start: "top 70%", // Triggers slightly lower for a reading-paced feel
                        toggleClass: "active-highlight",
                        once: true // Highlight only once
                    }
                });
            });

        }, containerRef);
        return () => ctx.revert();
    }, []);

    const contrastA = "Nasza ekipa z zasady odrzuca:".split(" ");
    const contrastB = "tanie zamienniki, taśmy i prowizoryczny silikon.".split(" ");

    const statementA = "My dostarczamy:".split(" ");
    const statementC = "Pewność na pokolenia.".split(" ");

    return (
        <section ref={containerRef} id="dlaczego-my" className="relative w-full py-32 md:py-48 bg-primary text-dark overflow-hidden flex items-center justify-center">
            {/* Texture BG */}
            <div
                className="parallax-bg absolute inset-[-20%] z-0 bg-cover bg-center opacity-10 grayscale"
                style={{ backgroundImage: "url('https://images.unsplash.com/photo-1518596645608-2e11897c92eb?q=80&w=2600&auto=format&fit=crop')" }}
            />

            <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" ref={textRef}>
                <div className="mb-12">
                    <p className="font-heading text-lg md:text-2xl text-dark/60 mb-2 uppercase tracking-wide flex flex-wrap justify-center gap-x-2">
                        {contrastA.map((w, i) => <span key={`ca-${i}`} className="word-reveal inline-block">{w}</span>)}
                    </p>
                    <p className="font-body text-xl md:text-3xl text-dark/80 line-through decoration-accent decoration-2 flex flex-wrap justify-center gap-x-2">
                        {contrastB.map((w, i) => <span key={`cb-${i}`} className="word-reveal inline-block">{w}</span>)}
                    </p>
                </div>

                <div className="mt-16 sm:mt-24 flex flex-col items-center">
                    <p className="font-heading text-xl md:text-3xl text-dark uppercase font-bold tracking-tight mb-4 flex flex-wrap justify-center gap-x-2">
                        {statementA.map((w, i) => <span key={`sa-${i}`} className="word-reveal inline-block">{w}</span>)}
                    </p>
                    {/* Hardcoded the main dramatic word to wrap it in the highlight mark */}
                    <div className="drama-text text-5xl sm:text-7xl md:text-9xl text-accent flex flex-wrap justify-center gap-x-4 md:gap-x-6 leading-none word-reveal">
                        <mark className="highlight-text bg-transparent text-accent relative inline-block before:content-[''] before:absolute before:inset-0 before:bg-accent/20 before:scale-x-0 before:origin-left before:transition-transform before:duration-1000 before:ease-[cubic-bezier(0.85,0,0.15,1)] [&.active-highlight]:before:scale-x-100 px-2 -mx-2">inżynierie.</mark>
                    </div>
                    <div className="w-full flex justify-center gap-x-4 md:gap-x-6 mt-4">
                        {statementC.map((w, i) => <span key={`sc-${i}`} className="word-reveal inline-block">{w}</span>)}
                    </div>
                </div>
            </div>
        </section>
    );
}
